
import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, MessageCircle, Loader2, Sparkles, Minimize2, AlertCircle } from 'lucide-react';
import { SiteSettings, NewsItem, Product, Business } from '../types';
import { getArticleUrl, getBasePath } from '../constants';
import { generateAIChatResponse } from '../services/aiFactory';
import { sanitizeHtml } from '../lib/security';

interface AiChatWidgetProps {
  settings: SiteSettings;
  currentArticle?: NewsItem | null;
  news: NewsItem[];
  products: Product[];
  businesses: Business[];
  forceOpen?: boolean; 
  onClose?: () => void; 
}

interface Message {
  role: 'user' | 'model';
  text: string;
}

const AiChatWidget: React.FC<AiChatWidgetProps> = ({ settings, currentArticle, news, products, businesses, forceOpen, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceOpen !== undefined && forceOpen !== isOpen) {
      setIsOpen(forceOpen);
    }
  }, [forceOpen]);

  useEffect(() => {
    if (isOpen) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  useEffect(() => {
      if (isOpen && messages.length === 0) {
          const siteTitle = settings.siteTitle || "este site";
          const welcome = currentArticle 
            ? `Olá! Sou o Mentor Digital de ${siteTitle}. Vi que você está interessado em **${currentArticle.title}**.\n\nComo posso te ajudar com essa leitura ou sugerir ferramentas de automação similares?`
            : `Olá! Sou o Mentor Digital de ${siteTitle}. Seja bem-vindo!\n\nPosso te apresentar nossos sistemas de automação, sugerir conteúdos do blog ou explicar como podemos transformar sua presença digital. Como deseja começar?`;
          
          setMessages([{ role: 'model', text: welcome }]);
      }
  }, [isOpen, currentArticle, settings.siteTitle]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const buildContext = () => {
      let ctx = `IDENTIDADE DO SITE:\n`;
      ctx += `- Título: ${settings.siteTitle || "Portal"}\n`;
      ctx += `- Descrição: ${settings.siteDescription || ""}\n`;
      if (settings.aboutPageContent) {
          ctx += `- Sobre: ${settings.aboutPageContent.substring(0, 300)}...\n`;
      }
      ctx += `- WhatsApp: ${settings.whatsappNumber || ""}\n`;
      ctx += `- Redes Sociais: ${settings.instagramUrl || ""} ${settings.youtubeUrl || ""} ${settings.facebookUrl || ""}\n\n`;

      if (currentArticle) {
          ctx += `USUÁRIO ESTÁ LENDO: "${currentArticle.title}". URL: ${getArticleUrl(currentArticle.slug || currentArticle.id, currentArticle.category)}\n\n`;
      }
      
      ctx += "PRODUTOS & SISTEMAS (Envie o link ao usuário se fizer sentido):\n";
      products.slice(0, 10).forEach(p => {
          const cleanDesc = p.description ? p.description.replace(/<[^>]*>/g, '') : '';
          ctx += `- ${p.title}: ${cleanDesc.substring(0, 80)}... Link: ${getBasePath()}/produtos/${p.slug || p.id}\n`;
      });

      if (businesses && businesses.length > 0) {
          ctx += "\nGUIA DE NEGÓCIOS / DIRETÓRIO:\n";
          businesses.slice(0, 5).forEach(b => {
              ctx += `- ${b.name}: ${b.description?.substring(0, 60)}... Link: ${getBasePath()}/guia/${b.category}/${b.slug || b.id}\n`;
          });
      }

      ctx += "\nINSIGHTS RECENTES DO BLOG:\n";
      news.filter(n => {
          const isLocalSeo = n.is_local_seo === true || (n.is_local_seo as any) === 1 || (n.is_local_seo as any) === '1';
          return !isLocalSeo;
      }).slice(0, 5).forEach(n => {
          ctx += `- ${n.title}. Link: ${getArticleUrl(n.slug || n.id, n.category)}\n`;
      });

      return ctx;
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!inputText.trim() || isLoading) return;

      const userMsg = inputText;
      setInputText('');
      setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
      setIsLoading(true);

      try {
          // Limpeza rigorosa do histórico para evitar erros de turnos (obrigatório para Gemini)
          const validHistory = [];
          const rawHistory = messages.filter(m => m.text.length > 0);
          
          // O histórico para a API deve ser filtrado para não incluir mensagens de erro ou saudações sem resposta do usuário
          // Começamos o histórico real a partir da primeira interação do usuário se possível
          let foundFirstUser = false;
          for(const m of rawHistory) {
              if (m.role === 'user') foundFirstUser = true;
              if (foundFirstUser) {
                  validHistory.push({
                      role: m.role,
                      parts: [{ text: m.text }]
                  });
              }
          }

          const context = buildContext();
          const responseText = await generateAIChatResponse(validHistory, userMsg, context, settings);

          setMessages(prev => [...prev, { role: 'model', text: responseText }]);
      } catch (error: any) {
          console.error("Chat API Error:", error);
          let errorMsg = "Tive um pequeno problema técnico na conexão com o motor de IA. Por favor, tente enviar sua pergunta novamente.";
          
          if (error.message?.includes("API key")) {
              errorMsg = "A chave de API configurada parece inválida ou expirou. Verifique o painel administrativo.";
          }
          
          setMessages(prev => [...prev, { role: 'model', text: errorMsg }]);
      } finally {
          setIsLoading(false);
      }
  };

  const renderFormattedText = (text: string, isModel: boolean) => {
      let html = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-black text-gray-900">$1</strong>');
      html = html.replace(/\n\n/g, '<div class="h-3"></div>');
      html = html.replace(/\n/g, '<br/>');
      const linkClass = isModel ? "text-primary font-bold hover:underline" : "text-white underline font-bold";
      
      // Suporte para Markdown links [texto](url)
      html = html.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" target="_blank" class="${linkClass}">$1</a>`);

      return <div className="text-[13px] md:text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
  };

  return (
    <div className={`hidden lg:flex fixed bottom-6 left-6 z-[150] flex flex-col items-start gap-4 font-sans`}>
        
        {/* Janela do Widget */}
        {isOpen && (
            <div className={`bg-white w-[calc(100vw-32px)] md:w-[420px] h-[550px] md:h-[650px] rounded-[32px] shadow-[0_30px_100px_rgba(0,0,0,0.35)] border border-gray-100 flex flex-col overflow-hidden animate-fade-in-up origin-bottom-left mb-2`}>
                
                <div className="bg-primary p-6 flex justify-between items-center text-white shrink-0 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12 scale-150 pointer-events-none">
                        <Bot size={80} />
                    </div>
                    
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="w-12 h-12 bg-white/20 rounded-2xl backdrop-blur-md flex items-center justify-center border border-white/30 shadow-inner">
                            <Bot size={24} />
                        </div>
                        <div>
                            <h3 className="font-black text-sm uppercase tracking-tight leading-none mb-1.5">Mentor Digital</h3>
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                                <p className="text-[10px] font-black uppercase tracking-widest opacity-80">{settings.siteTitle || "Suporte"} On-line</p>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={handleClose} 
                        className="p-2.5 hover:bg-white/10 rounded-xl transition-all active:scale-90 relative z-10"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/40 space-y-6 custom-scrollbar scroll-smooth">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                            <div className={`max-w-[90%] p-4 md:p-5 rounded-[24px] shadow-sm ${
                                msg.role === 'user' 
                                ? 'bg-primary text-white rounded-br-none shadow-primary/10' 
                                : 'bg-white text-gray-700 border border-gray-100 rounded-bl-none'
                            }`}>
                                {renderFormattedText(msg.text, msg.role === 'model')}
                            </div>
                        </div>
                    ))}
                    
                    {isLoading && (
                        <div className="flex justify-start animate-fade-in">
                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3">
                                <div className="flex gap-1.5">
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
                                </div>
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sincronizando...</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} className="h-2" />
                </div>

                <div className="p-5 bg-white border-t border-gray-100 shrink-0">
                    <form onSubmit={handleSendMessage} className="flex gap-3 items-center">
                        <input 
                            type="text" 
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Tire sua dúvida agora..."
                            className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 text-sm font-medium focus:outline-none focus:border-primary focus:bg-white transition-all shadow-inner"
                            disabled={isLoading}
                        />
                        <button 
                            type="submit" 
                            disabled={isLoading || !inputText.trim()}
                            aria-label="Enviar mensagem"
                            className="p-4 bg-primary text-white rounded-2xl hover:bg-primaryDark transition-all disabled:opacity-50 shadow-xl shadow-primary/20 transform active:scale-95"
                        >
                            <Send size={20} />
                        </button>
                    </form>
                    <p className="text-center text-[9px] text-gray-300 font-bold uppercase tracking-[0.2em] mt-4">Motor de Inteligência Híbrido Ativo</p>
                </div>
            </div>
        )}

        {/* Botão Flutuante */}
        <button 
            onClick={() => {
                const newState = !isOpen;
                setIsOpen(newState);
                if (!newState && onClose) onClose();
            }}
            aria-label={isOpen ? "Fechar assistente de conversa" : "Abrir assistente de conversa"}
            className={`hidden lg:flex w-14 h-14 md:w-16 md:h-16 rounded-full shadow-[0_15px_40px_rgba(37,99,235,0.35)] transition-all transform hover:scale-110 active:scale-90 items-center justify-center group relative overflow-hidden ${isOpen ? 'bg-white text-gray-400' : 'bg-primary text-white'}`}
        >
            {isOpen ? <Minimize2 size={24} /> : (
                <>
                    <Bot size={32} className="group-hover:scale-110 transition-transform" />
                    <div className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full animate-pulse"></div>
                </>
            )}
        </button>
    </div>
  );
};

export default AiChatWidget;
